/* eslint-disable security/detect-object-injection */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {createContext} from 'react';
import './ArrTabFocusProvider.scss';

interface ArrTabFocusContextType {
  id: string;
  isModalOpen: boolean;
  setModalOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
}
// --------------

interface ArrTabFocusProviderType extends React.PropsWithChildren {
  id: string;
  autoFocus?: boolean;
}
// --------------

export const ArrTabFocusContext = createContext<
  ArrTabFocusContextType | Record<string, any>
>({});

// -------------------------------------
// ArrTabFocusProvider Component
// -------------------------------------
const ArrTabFocusProvider: React.FC<ArrTabFocusProviderType> = ({
  children,
  id,
  ...props
}) => {
  const [isActive, setActive] = useState<boolean>(false);
  const [_activeElmId, setActiveElmId] = useState<string>('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const keyRef = useRef<string | null>(null);
  // --------------

  useEffect(() => {
    if (!id) {
      console.warn(
        'ArrTabFocusProvider requires a unique ID. No id is provided.'
      );
    }
  }, [id]);
  // --------------

  const getElement = useCallback(
    (id: string, index: number = 0) => {
      const activeElmId = document.activeElement?.getAttribute('data-tab-id');

      if (activeElmId) {
        const elms =
          containerRef.current?.querySelectorAll<HTMLElement>(
            `:scope [data-tab-group="${id}"]:not(:disabled)`
          ) ?? [];
        const activeIndex = Array.from(elms).findIndex(
          (elm) => elm.getAttribute('data-tab-id') === activeElmId
        );

        if (activeIndex !== -1 && elms[activeIndex + index]) {
          return {
            elm: elms[activeIndex + index],
            elms,
            elmId: elms[activeIndex + index]?.getAttribute('data-tab-id') || '',
          };
        }
        return {elm: null, elms};
      }
      return {elm: null, elms: []};
    },
    [containerRef.current]
  );
  // --------------

  const focusNext = useCallback(
    (id: string, ev: React.KeyboardEvent<HTMLElement>) => {
      const {elm: nextElement, elmId} = getElement(id, 1);

      if (nextElement) {
        ev.stopPropagation();
        ev.preventDefault();

        nextElement.setAttribute('tabindex', '0');
        nextElement.focus();
        setActiveElmId(elmId);
      }
    },
    [containerRef.current]
  );
  // --------------

  const focusPrevious = useCallback(
    (id: string, ev: React.KeyboardEvent<HTMLElement>) => {
      const {elm: prevElement, elmId} = getElement(id, -1);

      if (prevElement) {
        ev.stopPropagation();
        ev.preventDefault();

        prevElement.setAttribute('tabindex', '0');
        prevElement.focus();
        setActiveElmId(elmId);
      }
    },
    [containerRef.current]
  );
  // --------------

  const resetTabIndex = () => {
    const elms =
      containerRef.current?.querySelectorAll<HTMLElement>(
        `:scope [data-tab-group="${id}"]:not(:disabled)`
      ) ?? [];
    elms.forEach((elm) => elm.setAttribute('tabindex', '0'));
  };
  // --------------

  const focusParentTabElement = useCallback(() => {
    const parentTab =
      containerRef.current?.parentElement?.closest('[data-tab-id]');
    const parentTabId = parentTab?.getAttribute('data-tab-id') || '';
    const parentElm = containerRef.current?.parentElement?.closest(
      `[data-tab-id="${parentTabId}"]`
    ) as HTMLElement;
    parentElm?.focus();
  }, [containerRef.current]);
  // --------------

  const handleKeyPress = useCallback(
    (ev: React.KeyboardEvent<HTMLElement>) => {
      keyRef.current = ev.key;

      /* ---- Shift + Tab ---- */
      if (ev.key === 'Tab' && ev.shiftKey) {
        focusPrevious(id, ev);
        return;
      }
      /* ---- Tab ---- */
      if (ev.key === 'Tab') {
        focusNext(id, ev);
        return;
      }

      /* ---- Enter or Return ---- */
      if (ev.key === 'Enter') {
        const {elm: currentElement} = getElement(id, 0);

        if (currentElement) {
          const firstChild = currentElement.querySelector<HTMLElement>(
            ':scope [tabindex="0"]'
          );
          firstChild?.focus();
        } else {
          const activeElm = document.activeElement as HTMLElement;
          activeElm?.click();
        }
        return;
      }

      /* ---- Escape ---- */
      if (ev.key === 'Escape') {
        focusParentTabElement();
      }
    },
    [containerRef.current, isActive]
  );
  // --------------

  const handleFocus = (ev: React.FocusEvent<HTMLElement>) => {
    ev.stopPropagation();

    if (!isActive) {
      setActive(true);
    }
  };
  // --------------

  const handleBlur = (ev: React.FocusEvent<HTMLElement>) => {
    ev.stopPropagation();

    if (!ev.currentTarget.contains(ev.relatedTarget) && isActive) {
      if (keyRef.current === 'Tab') {
        keyRef.current = null;
      }

      const nextPresentationElm = ev.relatedTarget?.closest(
        '[role="presentation"]'
      );
      const nextProviderElm = ev.relatedTarget?.closest('[data-tab-group-id]');

      if (nextProviderElm) {
        focusParentTabElement();
      }

      if (!nextPresentationElm) {
        setActive(false);
        resetTabIndex();
        setActiveElmId('');

        ev.preventDefault();
      }
    }
  };
  // --------------

  const handleClick = (ev: React.MouseEvent<HTMLElement>) => {
    if (!containerRef.current?.contains(ev.currentTarget)) {
      // Clicked Outside
      setActive(false);
      resetTabIndex();
      setActiveElmId('');
    }
  };
  // --------------

  useEffect(() => {
    if (containerRef.current) {
      // Set the first element focusable
      containerRef.current
        ?.querySelector(`[data-tab-group="${id}"]`)
        ?.setAttribute('tabindex', '0');
    }
  }, [containerRef.current]);
  // --------------

  useEffect(() => {
    if (!isModalOpen && _activeElmId) {
      const activeElm = containerRef.current?.querySelector<HTMLElement>(
        `[data-tab-id=${_activeElmId}]`
      );
      activeElm?.focus();
    }
  }, [isModalOpen]);
  // --------------

  return (
    <ArrTabFocusContext
      value={{
        id,
        isModalOpen,
        setModalOpen,
      }}
    >
      <div
        className={`arr-tab-focus-provider ${isActive ? 'active' : 'inactive'}`}
        ref={containerRef}
        tabIndex={-1}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyPress}
        onClick={handleClick}
        role="button"
        data-tab-group-id={id}
        data-tab-id={id}
        data-testid={`arr-tab-focus-provider-${id}`}
        {...props}
      >
        {children}
      </div>
    </ArrTabFocusContext>
  );
};

export {ArrTabFocusProvider};
export default ArrTabFocusProvider;
